import PropTypes from "prop-types";
import { Box } from "@mui/material";

/**
 * The label above a form field.
 *
 * It exists as its own component because not every field in these dialogs is a
 * `DialogTextField` — the bug report has a screenshot uploader, the resume form
 * has a file picker — and those were each labelled by a hand-written
 * `<Typography>` with its own size, weight and colour. Three labels styled three
 * ways in one dialog is most of what "scattered" describes, and it is the kind
 * of drift that only gets worse as forms are added.
 *
 * Pass `htmlFor` whenever the label names a single control, so the association
 * is native rather than visual.
 */

/* Centred rather than baseline-aligned. A label that carries an icon is an
   inline-flex box whose baseline comes from the icon, not from its text, so
   baseline alignment drops the "Optional" tag below the label it belongs to. */
const rowSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
};

const labelSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  fontSize: "0.8rem",
  fontWeight: 600,
  lineHeight: 1.4,
  color: "text.primary",
};

const optionalSx = {
  fontSize: "0.7rem",
  fontWeight: 500,
  lineHeight: 1.4,
  color: "text.secondary",
};

function FieldLabel({ htmlFor, required = false, optional = false, icon, children, sx }) {
  return (
    <Box sx={{ ...rowSx, ...sx }}>
      <Box component={htmlFor ? "label" : "span"} htmlFor={htmlFor} sx={labelSx}>
        {icon}
        <Box component="span">
          {children}
          {required ? (
            // Decorative. The control carries the real `required` attribute, so
            // announcing the asterisk too would just be noise.
            <Box
              component="span"
              aria-hidden="true"
              sx={{ color: "error.text", ml: 0.375, fontSize: "0.9em", verticalAlign: "top" }}
            >
              *
            </Box>
          ) : null}
        </Box>
      </Box>
      {optional ? <Box component="span" sx={optionalSx}>Optional</Box> : null}
    </Box>
  );
}

FieldLabel.propTypes = {
  htmlFor: PropTypes.string,
  required: PropTypes.bool,
  /** Renders a quiet "Optional" tag beside the label. */
  optional: PropTypes.bool,
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

export default FieldLabel;
